import React, { use, useState } from "react";
import {
  Table,
  Badge,
  Form,
  Button,
  Card,
  Modal,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Save, Plus, Edit2, Check, X } from "lucide-react";

import { InputGroup } from "react-bootstrap";
import { setStepIndex, setStepValid } from "../../../redux/globalJoyrideSlice";
import { useDispatch } from "react-redux";

const ProposedClassesTab = ({
  proposedClasses,
  subjects,
  teachers,
  loading,
  handleProposedClassToggle,
  handleSectionsChange,
  handleSubjectToggle,
  handleSaveProposedClasses,
  handleTeacherChange,
  setClassStepIndex,
}) => {
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [currentClassId, setCurrentClassId] = useState(null);
  const [currentClassSubjectsConfig, setCurrentClassSubjectsConfig] = useState(
    []
  );
  const [activeClassId, setActiveClassId] = useState(null);
  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const dispatch = useDispatch();

  const openSubjectModal = (classId) => {
    const classConfig = proposedClasses.find((c) => c.id === classId);
    if (!classConfig) return;

    const subjectsConfig = subjects.map((subject) => {
      const isSelected = classConfig.selectedSubjects?.includes(
        subject.subject_id
      );
      const periodInfo = classConfig.subjectPeriods?.[subject.subject_id] || {};

      return {
        subject_id: subject.subject_id,
        subject_name: subject.subject_name,
        selected: isSelected,
        max_periods_per_week: periodInfo.max_periods_per_week || 0,
        theoretical_classes: periodInfo.theoretical_classes || 0,
        practical_classes: periodInfo.practical_classes || 0,
      };
    });

    setCurrentClassSubjectsConfig(subjectsConfig);
    setCurrentClassId(classId);
    setShowSubjectModal(true);
  };

  const closeSubjectModal = () => {
    setShowSubjectModal(false);
    setCurrentClassId(null);
    setCurrentClassSubjectsConfig([]);
  };

  const saveSubjectConfig = () => {
    const selectedSubjects = currentClassSubjectsConfig
      .filter((sub) => sub.selected)
      .map((sub) => sub.subject_id);

    const subjectPeriods = currentClassSubjectsConfig
      .filter((sub) => sub.selected)
      .reduce((acc, sub) => {
        acc[sub.subject_id] = {
          max_periods_per_week: sub.max_periods_per_week,
          theoretical_classes: sub.theoretical_classes,
          practical_classes: sub.practical_classes,
        };
        return acc;
      }, {});

    handleSubjectToggle(currentClassId, selectedSubjects, subjectPeriods);
    closeSubjectModal();

    setTimeout(() => {
      const el = document.querySelector(".save-config-step");

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      setTimeout(() => {
        dispatch(setStepIndex(9)); // ✅ correct step
        window.dispatchEvent(new Event("resize"));
      }, 300);
    }, 300);
  };

  const handleSubjectSelectionChange = (subjectId, isSelected) => {
    setActiveSubjectId(isSelected ? subjectId : null);

    setCurrentClassSubjectsConfig((prev) =>
      prev.map((sub) =>
        sub.subject_id === subjectId
          ? {
            ...sub,
            selected: isSelected,
            max_periods_per_week: isSelected
              ? sub.max_periods_per_week || 5
              : 0,
            theoretical_classes: isSelected
              ? sub.theoretical_classes || 5
              : 0,
            practical_classes: isSelected ? sub.practical_classes || 0 : 0,
          }
          : sub
      )
    );
    if (isSelected) {
      setTimeout(() => {
        dispatch(setStepIndex(6)); // 🎯 your "periods" step index
        window.dispatchEvent(new Event("resize"));
      }, 200);
    }
  };

  const handlePeriodChange = (subjectId, field, value) => {
    setCurrentClassSubjectsConfig((prev) =>
      prev.map((subject) => {
        if (subject.subject_id === subjectId) {
          const updatedSubject = {
            ...subject,
            [field]: parseInt(value),
          };

          // Auto-update theoretical_classes if field is max_periods_per_week
          // and user hasn't manually updated theoretical_classes
          if (
            field === "max_periods_per_week" &&
            !subject.isTheoreticalTouched
          ) {
            updatedSubject.theoretical_classes = parseInt(value);
          }

          // If user manually updated theoretical_classes, set the flag
          if (field === "theoretical_classes") {
            updatedSubject.isTheoreticalTouched = true;
          }

          return updatedSubject;
        }
        return subject;
      })
    );
  };

  return (
    <>
      {/*<div className="class-list-step" />*/}
      <Card className="border-0 shadow-sm  ">
        <Card.Header
          className="bg-primary text-white d-flex justify-content-between align-items-center "
          style={{
            // backgroundColor: "#1e88e5",
            // background: "linear-gradient(to right, #7B2FF7, #F40076)",
            background: "linear-gradient(to right, #7B2FF7 50%, #F40076 100%)",
            fontSize: "0.85rem",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          }}
        >
          <h5 className="mb-0 fw-semibold ">Select Class</h5>
        </Card.Header>
        <Card.Body>
          {loading && (
            <div className="text-center py-4 ">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 mb-0">
                Please wait while we configure classes and assign teachers to
                their respective sections...
              </p>
            </div>
          )}

          {!loading && proposedClasses.length === 0 && (
            <Alert variant="info" className="text-center">
              No classes available to configure. Please add classes first.
            </Alert>
          )}

          {!loading && proposedClasses.length > 0 && (
            <>
              <div className="repeat-confirm-step" />
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "60px" }}>Active</th>
                      <th>Class</th>
                      <th style={{ width: "120px" }}>Sections</th>
                      <th>Class Teachers</th>
                      <th style={{ width: "150px" }}>Subjects</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposedClasses.map((cls) => (
                      <tr
                        key={cls.id}
                        className={cls.selected ? "bg-light" : ""}
                      >
                        <td>
                          <Form.Check
                            type="checkbox"
                            id={`checkbox-${cls.id}`}
                            checked={cls.selected}
                            className={!cls.selected ? "class-active-step" : ""}
                            inputProps={{ style: { border: "2px solid #444", width: "18px", height: "18px", cursor: "pointer" } }}
                            onChange={() => {
                              handleProposedClassToggle(cls.id);
                              setActiveClassId(cls.id);          // 🎯 mark this class as active flow
                              setTimeout(() => {
                                dispatch(setStepIndex(2));            // 👉 move Joyride to next step
                                window.dispatchEvent(new Event("resize"));
                              }, 200);
                            }}

                            label=""
                          />
                        </td>
                        <td>
                          <span className="fw-semibold">{cls.name}</span>
                        </td>
                        <td>
                          {cls.selected ? (
                            <Form.Control
                              type="number"
                              min="1"
                              max="10"
                              className={`w-100 ${cls.id === activeClassId ? "class-sections-step" : ""}`}
                              value={cls.sections}
                              onChange={(e) => {
                                handleSectionsChange(
                                  cls.id,
                                  Math.max(
                                    0,
                                    Math.min(10, parseInt(e.target.value) || "")
                                  )
                                );
                                dispatch(setStepValid(true));
                              }}
                              size="sm"
                            />
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {cls.selected ? (
                            <div className="d-flex flex-column gap-2">
                              {cls.sectionNames?.map((sectionName, index) => (
                                <div
                                  key={index}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                    {sectionName}
                                  </span>
                                  <Form.Select
                                    size="sm"
                                    value={
                                      cls.localSectionTeachers?.[
                                      `section-${index}`
                                      ] || ""
                                    }
                                    onChange={(e) => {
                                      handleTeacherChange(
                                        cls.id,
                                        index,
                                        e.target.value
                                      );
                                      dispatch(setStepValid(true));   // 🔓 unlock Next
                                    }}
                                    required
                                    className={`flex-grow-1 ${cls.id === activeClassId ? "class-teacher-step" : ""}`}
                                  >
                                    <option value="">Select teacher...</option>
                                    {teachers.length > 0 && teachers?.map((t) => (
                                      <option
                                        key={t.teacher_id}
                                        value={t.teacher_id}
                                      >
                                        {t.name}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted">Not active</span>
                          )}
                        </td>
                        <td>
                          {cls.selected ? (
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                variant={
                                  cls.selectedSubjects?.length
                                    ? "outline-success"
                                    : "outline-primary"
                                }
                                size="sm"
                                onClick={() => {
                                  openSubjectModal(cls.id);

                                  setTimeout(() => {
                                    dispatch(setStepIndex(5)); // 👉 move to "select subjects"
                                    window.dispatchEvent(new Event("resize"));
                                  }, 300);
                                }}

                                className={`d-flex align-items-center ${cls.id === activeClassId ? "add-edit-subject-step" : ""
                                  }`}

                              >
                                {cls.selectedSubjects?.length ? (
                                  <>
                                    <Edit2 size={16} className="me-1" />
                                    {cls.selectedSubjects.length} subjects
                                  </>
                                ) : (
                                  <>
                                    <Plus size={16} className="me-1" />
                                    Add Subjects
                                  </>
                                )}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-end mt-4">
                <Button
                  variant="primary"
                  onClick={handleSaveProposedClasses}
                  disabled={
                    loading ||
                    proposedClasses.filter((c) => c.selected).length === 0
                  }
                  className="d-flex align-items-center gap-2 px-4 py-2 save-config-step"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                       Save Class Settings
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Subject Configuration Modal */}
      <Modal
        show={showSubjectModal}
        onHide={closeSubjectModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-semibold">
            <span className="text-primary">Subject Configuration</span> -{" "}
            {proposedClasses.find((c) => c.id === currentClassId)?.name ||
              "Class"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <div className="select-subjects-step" style={{ height: "1px" }} />
          <Table hover responsive>
            <thead className="table-light">
              <tr>
                <th style={{ width: "50px" }}></th>
                <th>Subject</th>
                <th style={{ width: "150px" }}>Max Periods/Week</th>
                <th style={{ width: "150px" }}>Theoretical</th>
                <th style={{ width: "150px" }}>Practical</th>
              </tr>
            </thead>
            <tbody>
              {currentClassSubjectsConfig.map((subject) => (
                <tr key={subject.subject_id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={subject.selected}
                      inputProps={{ style: { border: "2px solid #444", width: "18px", height: "18px", cursor: "pointer" } }}
                      onChange={(e) =>
                        handleSubjectSelectionChange(
                          subject.subject_id,
                          e.target.checked,
                        )
                      }
                    //  className={`form-check-lg ${!subject.selected ? "select-subjects-step" : ""}`}
                    />
                  </td>
                  <td
                    className={subject.selected ? "fw-semibold" : "text-muted"}
                  >
                    {subject.subject_name}
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      max="20"
                      value={subject.max_periods_per_week}
                      onChange={(e) => {
                        handlePeriodChange(
                          subject.subject_id,
                          "max_periods_per_week",
                          e.target.value
                        );
                        dispatch(setStepValid(true));   // 🔓 unlock Next
                      }}

                      disabled={!subject.selected}
                      className={`${subject.subject_id === activeSubjectId ? "subject-periods-step" : ""
                        } ${!subject.selected ? "bg-light" : ""}`}

                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      max={subject.max_periods_per_week}
                      value={subject.theoretical_classes}
                      onChange={(e) => {
                        handlePeriodChange(
                          subject.subject_id,
                          "theoretical_classes",
                          e.target.value
                        );
                        dispatch(setStepValid(true));   // 🔓 unlock Next
                      }}
                      disabled={!subject.selected}
                      className={`${subject.subject_id === activeSubjectId ? "subject-periods-step" : ""
                        } ${!subject.selected ? "bg-light" : ""}`}

                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      max={subject.max_periods_per_week}
                      value={subject.practical_classes}
                      onChange={(e) => {
                        handlePeriodChange(
                          subject.subject_id,
                          "practical_classes",
                          e.target.value
                        );
                        dispatch(setStepValid(true));   // 🔓 unlock Next
                      }}
                      disabled={!subject.selected}
                      className={`${subject.subject_id === activeSubjectId ? "subject-periods-step" : ""
                        } ${!subject.selected ? "bg-light" : ""}`}

                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Badge bg="light" text="dark" className="me-2">
              Selected:{" "}
              {currentClassSubjectsConfig.filter((s) => s.selected).length}
            </Badge>
            <Badge bg="light" text="dark">
              Total Subjects: {currentClassSubjectsConfig.length}
            </Badge>
          </div>
          <div>
            <Button
              variant="outline-secondary"
              onClick={closeSubjectModal}
              className="me-2"
            >
              <X size={18} className="me-1" /> Cancel
            </Button>
            <Button variant="primary" onClick={saveSubjectConfig} className="save-subject-config-step">
              <Check size={18} className="me-1" />Save Class Settings
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProposedClassesTab;
